import { CustomFieldDefinition } from '@fukuri-admins/apis/client/gen/model';
import { match } from 'ts-pattern';
import { GiftPlan } from '~/types/resources/GiftPlan';

/**
 * ギフトプランをフォーマットして文字列として返す
 *
 * @param giftPlan - ギフトプラン情報（nullの場合は「ギフトなし」を返す）
 * @returns フォーマット済みのギフトプラン文字列
 * @example
 * formatGiftPlan({ amount: 1000, months_interval: 3 })
 * // => "1000円（3ヶ月に1回 利用可）"
 */
export const formatGiftPlan = (giftPlan: GiftPlan | null): string =>
  giftPlan ? `${giftPlan.amount}円（${giftPlan.months_interval}ヶ月に1回 利用可）` : 'ギフトなし';

/**
 * 人数をフォーマットして文字列として返す
 *
 * @param count - 人数（nullの場合は「-」を返す）
 * @returns フォーマット済みの人数文字列
 * @example
 * formatNumberOfPeople(10)
 * // => "10人"
 */
export const formatNumberOfPeople = (count: number | null): string =>
  count !== null ? `${count}人` : '-';

/**
 * カスタムフィールドの表示値を取得する
 *
 * カスタムフィールドの入力タイプ（input_type）および値タイプ（value_type）に応じて、
 * 適切な表示用の文字列に変換する。
 *
 * @param field - カスタムフィールド定義
 * @param value - カスタムフィールドの値（カンマ区切りの場合あり）
 * @returns 表示用に変換された文字列
 *
 * @example
 * // 単一選択の場合: optionsからラベルを取得
 * getDisplayValue({ input_type: 'choice', options: { '1': '選択肢A' } }, '1') // => '選択肢A'
 *
 * // 複数選択の場合: カンマ区切りの値をそれぞれ変換して結合
 * getDisplayValue({ input_type: 'multi_choice', options: { '1': 'A', '2': 'B' } }, '1,2') // => 'A, B'
 *
 * // 真偽値の場合: 'true'/'false' を日本語に変換
 * getDisplayValue({ value_type: 'bool' }, 'true') // => 'はい'
 */
export const formatCustomFieldValue = (field: CustomFieldDefinition, value?: string): string => {
  // 値が存在しない場合は空文字列またはダッシュを返す
  if (!value) return '-';

  return (
    match(field)
      .returnType<string>()
      // 単一選択（choice）: optionsマップから対応するラベルを取得
      .with({ input_type: 'choice' }, field => field.options?.[value]?.toString() ?? value)
      // 複数選択（multi_choice）: カンマ区切りの各値をoptionsから変換し、読点区切りで結合
      .with({ input_type: 'multi_choice' }, field =>
        value
          .split(',')
          .map(value => value.trim())
          .map(item => field.options?.[item] ?? item)
          .join(', ')
      )
      // 真偽値（bool）: 'true' → 'はい', 'false' → 'いいえ' に変換
      .with({ value_type: 'bool' }, () =>
        match(value)
          .with('true', () => 'はい')
          .with('false', () => 'いいえ')
          .otherwise(() => value)
      )
      // その他（plain テキストなど）: 値をそのまま返す
      .otherwise(() => value)
  );
};
